const slideDuration: number = 5000; // 5 seconds
const swipeThreshold: number = 50;

export class Carousel {
	private static readonly carousels: Carousel[] = [];

	public static init(): void {
		const elements = document.getElementsByClassName('carousel');
		for (const element of elements) {
			this.carousels.push(new Carousel(element as HTMLDivElement));
		};
	}

	public readonly listElement: HTMLUListElement = this.element.children[0] as HTMLUListElement;
	public readonly pagerElement: HTMLUListElement = document.createElement('ul');
	public readonly slides: Slide[] = [];
	public timer: number = null;
	public current: Slide;

	private readonly slideDuration: number = slideDuration;
	private readonly swipeThreshold: number = swipeThreshold;
	private touchStartX: number = 0;
	private touchStartY: number = 0;
	private disableTouch: boolean = false;

	constructor(private readonly element: HTMLDivElement) {
		for (const slide of this.listElement.children) {
			this.slides.push(new Slide(this, slide as HTMLLIElement));
		}
		this.current = this.slides[0];
		this.current.activate();
		this.start();

		this.pagerElement.className = 'pager';
		this.element.append(this.pagerElement);
		this.element.addEventListener('touchstart', (event) => this.touchStart(event));
		this.element.addEventListener('touchmove', (event) => this.touchMove(event));
		this.element.addEventListener('touchend', (event) => this.touchEnd(event));
	}

	public start(): void {
		this.stop();
		this.timer = window.setTimeout(() => {
			this.next.activate();
			this.start();
		}, this.slideDuration);
	}

	public stop(): void {
		if (this.timer) {
			clearTimeout(this.timer);
			this.timer = null;
		}
	}

	private drag(delta: number): void {
		if (delta > 0 ? !this.current.index : !this.next.index) delta = delta / 2;
		this.listElement.style.left = `calc(${-100 * this.current.index}% + ${delta}px)`;
	}

	private touchStart(event: TouchEvent): void {
		this.element.classList.add('touch');
		this.stop();
		this.disableTouch = false;
		this.touchStartX = event.touches[0].clientX;
		this.touchStartY = event.touches[0].clientY;
	}

	private touchMove(event: TouchEvent): void {
		if (event.touches.length > 1 || this.disableTouch) return;

		const deltaX = event.touches[0].clientX - this.touchStartX;
		const deltaY = event.touches[0].clientY - this.touchStartY;
		if (Math.abs(deltaY) > this.swipeThreshold) {
			this.disableTouch = true;
			this.element.classList.remove('touch');
		}
		this.drag(this.disableTouch ? 0 : deltaX);
		event.preventDefault();
	}

	private touchEnd(event: TouchEvent): void {
		this.element.classList.remove('touch');
		this.drag(0);
		if (!this.disableTouch) {
			const delta = event.changedTouches[0].clientX - this.touchStartX;
			if (delta > this.swipeThreshold && this.current.index) {
				this.previous.activate();
			} else if (delta < -this.swipeThreshold && this.next.index) {
				this.next.activate();
			}
		}
		this.start();
	}

	public get next(): Slide {
		return this.slides[(this.current.index + 1) % this.slides.length];
	}

	public get previous(): Slide {
		return this.slides[(this.current.index + this.slides.length - 1) % this.slides.length];
	}
}

class Slide {
	private readonly pagerDotElement: HTMLLIElement = document.createElement('li');
	public readonly index: number = this.carousel.slides.length;

	constructor(private readonly carousel: Carousel, private readonly element: HTMLLIElement) {
		this.carousel.pagerElement.append(this.pagerDotElement);
		this.pagerDotElement.addEventListener('click', () => {
			this.activate();
			this.carousel.start();
		});
	}

	public activate() {
		this.carousel.current = this;
		for (const slide of this.carousel.slides) slide.pagerDotElement.classList.toggle('active', slide.isActive);
		this.carousel.listElement.style.left = `${-100 * this.index}%`;
	}

	public get isActive(): boolean {
		return this.carousel.current === this;
	}
}
